import { useEffect, useState, useRef } from "react";
import "./Warehouse.css";
import ContactForm from "../../components/ContactUs/ContactUs";
import WarehouseImage from "../../assets/images/warehouse.png";
import {
  CheckCircle,
  Smartphone,
  Package,
  Truck,
  BarChart3,
  Users,
  Scan,
  ArrowRight,
  Monitor,
  Zap,
  Settings,
} from "lucide-react";

const Warehouse = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showContact, setShowContact] = useState(false);
  const [visibleSections, setVisibleSections] = useState(new Set());

  const sectionRefs = {
    implementation: useRef(null),
    mobile: useRef(null),
    features: useRef(null),
    navFeatures: useRef(null),
    benefits: useRef(null),
    cta: useRef(null),
  };

  // Inline scroll to top functionality and trigger hero animations
  useEffect(() => {
    window.scrollTo(0, 0);

    // Trigger hero animations
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, []);

  // Intersection Observer for scroll-triggered animations
  useEffect(() => {
    const observerOptions = {
      threshold: 0.1,
      rootMargin: "0px 0px -50px 0px",
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const sectionName = entry.target.getAttribute("data-section");
          if (sectionName) {
            setVisibleSections((prev) => new Set([...prev, sectionName]));
          }
        }
      });
    }, observerOptions);

    // Observe all sections
    Object.values(sectionRefs).forEach((ref) => {
      if (ref.current) {
        observer.observe(ref.current);
      }
    });

    return () => observer.disconnect();
  }, []);

  const mobileFeatures = [
    {
      icon: Package,
      title: "Put-Away Operations",
      description:
        "Create work orders to check goods from suppliers, scan items and shelves for accurate placement and shipping.",
    },
    {
      icon: Scan,
      title: "Picking Operations",
      description:
        "Handle sales orders by delegating workers to scan bin codes, pick up goods, and ship them out efficiently.",
    },
    {
      icon: Truck,
      title: "Receiving & Shipping",
      description:
        "Manage both buy and sell operations through dedicated Whse Receipt or Whse Shipping work orders.",
    },
    {
      icon: ArrowRight,
      title: "Inventory Movements",
      description:
        "Change item locations by scanning bin codes and moving merchandise to new designated locations.",
    },
    {
      icon: BarChart3,
      title: "Label Printing",
      description:
        "Generate barcodes for pallets containing complete item information, all integrated with mobile application.",
    },
  ];

  const navFeatures = [
    {
      icon: Settings,
      title: "Warehouse Management",
      description:
        "Tightly integrate order processing, manufacturing, and warehouse functionality with FIFO, FEFO, or LILO prioritization methods.",
    },
    {
      icon: Package,
      title: "Internal Pick/Put-away",
      description:
        "Maintain accurate inventory records for testing, display, or operational needs independently of source documents.",
    },
    {
      icon: Monitor,
      title: "ADCS Support",
      description:
        "Improve inventory visibility and warehouse efficiency with Automated Data Collection System integration.",
    },
  ];

  const benefits = [
    "Optimize warehouse space with efficient pick patterns and bin quantities",
    "Streamline operations and increase productivity with directed workflows",
    "Improve order fulfillment with automatic pick and process status updates",
    "Gain flexibility for growth with scalable solutions",
    "Reduce wasted, lost, and damaged stock significantly",
    "Increase warehouse turns and delivery reliability",
    "Access real-time information with integrated reporting",
    "Support wireless devices for mobile warehouse operations",
  ];

  return (
    <div className="warehouse-main-container">
      {/* Hero Section */}
      <section className="warehouse-hero-wrapper">
        <div className="warehouse-hero-background">
          <div className="warehouse-floating-element warehouse-element-1"></div>
          <div className="warehouse-floating-element warehouse-element-2"></div>
          <div className="warehouse-floating-element warehouse-element-3"></div>
          <div className="warehouse-circle-decoration-one"></div>
          <div className="warehouse-circle-decoration-two"></div>
        </div>

        <div className="warehouse-content-width">
          <div className="warehouse-center-align">
            <h1
              className={`warehouse-hero-main-title ${
                isVisible ? "warehouse-slide-up" : ""
              }`}
            >
              Advanced{" "}
              <span className="warehouse-gradient-text">
                Warehouse Management
              </span>
            </h1>
            <p
              className={`warehouse-hero-subtitle ${
                isVisible ? "warehouse-fade-in" : ""
              }`}
            >
              The warehouse industry with the prospect of on-demand living has
              advanced significantly. Time, Efficiency, and Workflow are
              critical factors that serve our company efficiently through the
              SELF MANAGEMENT module, ensuring work is carried out as quickly
              and accurately as possible.
            </p>
            <div
              className={`warehouse-hero-buttons ${
                isVisible ? "warehouse-bounce-in" : ""
              }`}
            >
              <button
                className="warehouse-primary-button"
                onClick={() => (window.location.href = "/products/portfolio")}
              >
                <Zap className="warehouse-small-icon" />
                Explore Solutions
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Implementation Section */}
      <section
        className="warehouse-implementation-area"
        ref={sectionRefs.implementation}
        data-section="implementation"
      >
        <div className="warehouse-content-width">
          <div className="warehouse-center-align">
            <h2
              className={`warehouse-implementation-heading ${
                visibleSections.has("implementation")
                  ? "warehouse-slide-up"
                  : ""
              }`}
            >
              Automated Implementation Permissions
            </h2>
            <p
              className={`warehouse-implementation-description ${
                visibleSections.has("implementation") ? "warehouse-fade-in" : ""
              }`}
            >
              Your Warehouse Management module offers repeatedly automated
              choices including Warehouse Setup, Warehouse Trade, Warehouse
              Acceptance, Warehouse Transport, Moves, and Label Printing.
            </p>
          </div>
          <div
            className={`warehouse-streamline-image ${
              visibleSections.has("implementation") ? "warehouse-scale-in" : ""
            }`}
          >
            <img
              src={WarehouseImage || "/placeholder.svg"}
              alt="Modern warehouse operations with workers using mobile devices for inventory management"
              className="warehouse-main-image"
            />
          </div>
        </div>
      </section>

      {/* Mobile Management Section */}
      <section
        className="warehouse-mobile-area"
        ref={sectionRefs.mobile}
        data-section="mobile"
      >
        <div className="warehouse-content-width">
          <div className="warehouse-mobile-layout">
            <div
              className={`warehouse-mobile-content ${
                visibleSections.has("mobile") ? "warehouse-slide-left" : ""
              }`}
            >
              <span className="warehouse-mobile-badge">Mobile Integration</span>
              <h2 className="warehouse-mobile-heading">
                Warehouse Management through Mobile
              </h2>
              <p className="warehouse-mobile-description">
                Complete warehouse operations through mobile devices with
                integrated scanning, picking, and shipping capabilities. All
                functions are seamlessly linked in our mobile application for
                maximum efficiency and accuracy.
              </p>
              <div className="warehouse-mobile-highlights">
                <div className="warehouse-mobile-highlight">
                  <CheckCircle className="warehouse-check-icon" />
                  <span>Real-time scanning</span>
                </div>
                <div className="warehouse-mobile-highlight">
                  <CheckCircle className="warehouse-check-icon" />
                  <span>Integrated workflows</span>
                </div>
              </div>
            </div>
            <div
              className={`warehouse-mobile-visual ${
                visibleSections.has("mobile") ? "warehouse-float-up" : ""
              }`}
            >
              <div className="warehouse-mobile-card">
                <Smartphone className="warehouse-large-icon" />
                <h3 className="warehouse-mobile-card-heading">
                  Mobile Warehouse Operations
                </h3>
                <div className="warehouse-mobile-features-list">
                  <div className="warehouse-mobile-feature-point">
                    <div className="warehouse-feature-dot"></div>
                    <span className="warehouse-feature-point-text">
                      Barcode scanning
                    </span>
                  </div>
                  <div className="warehouse-mobile-feature-point">
                    <div className="warehouse-feature-dot"></div>
                    <span className="warehouse-feature-point-text">
                      Work order management
                    </span>
                  </div>
                  <div className="warehouse-mobile-feature-point">
                    <div className="warehouse-feature-dot"></div>
                    <span className="warehouse-feature-point-text">
                      Inventory tracking
                    </span>
                  </div>
                  <div className="warehouse-mobile-feature-point">
                    <div className="warehouse-feature-dot"></div>
                    <span className="warehouse-feature-point-text">
                      Label printing
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile Features Section */}
      <section
        className="warehouse-features-area"
        ref={sectionRefs.features}
        data-section="features"
      >
        <div className="warehouse-content-width">
          <div className="warehouse-center-align">
            <h2
              className={`warehouse-features-heading ${
                visibleSections.has("features") ? "warehouse-slide-up" : ""
              }`}
            >
              Mobile Warehouse Functions
            </h2>
            <p
              className={`warehouse-features-description ${
                visibleSections.has("features") ? "warehouse-fade-in" : ""
              }`}
            >
              Comprehensive mobile solutions for all warehouse operations, from
              receiving to shipping.
            </p>
          </div>

          <div className="warehouse-features-layout">
            {mobileFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`warehouse-feature-item ${
                    visibleSections.has("features") ? "warehouse-slide-up" : ""
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="warehouse-feature-item-top">
                    <div className="warehouse-feature-item-icon">
                      <Icon className="warehouse-medium-icon" />
                    </div>
                    <h3 className="warehouse-feature-item-title">
                      {feature.title}
                    </h3>
                  </div>
                  <div className="warehouse-feature-item-body">
                    <p className="warehouse-feature-item-text">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* NAV Features Section */}
      <section
        className="warehouse-nav-features-area"
        ref={sectionRefs.navFeatures}
        data-section="navFeatures"
      >
        <div className="warehouse-content-width">
          <div className="warehouse-center-align">
            <h2
              className={`warehouse-nav-features-heading ${
                visibleSections.has("navFeatures") ? "warehouse-slide-up" : ""
              }`}
            >
              Dynamics NAV Warehouse Functionality
            </h2>
            <p
              className={`warehouse-nav-features-description ${
                visibleSections.has("navFeatures") ? "warehouse-fade-in" : ""
              }`}
            >
              Comprehensive warehouse management capabilities built into
              Microsoft Dynamics NAV.
            </p>
          </div>

          <div className="warehouse-nav-features-layout">
            {navFeatures.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className={`warehouse-nav-feature-item ${
                    visibleSections.has("navFeatures")
                      ? "warehouse-slide-up"
                      : ""
                  }`}
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="warehouse-nav-feature-item-top">
                    <div className="warehouse-nav-feature-item-icon">
                      <Icon className="warehouse-medium-icon" />
                    </div>
                    <h3 className="warehouse-nav-feature-item-title">
                      {feature.title}
                    </h3>
                  </div>
                  <div className="warehouse-nav-feature-item-body">
                    <p className="warehouse-nav-feature-item-text">
                      {feature.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section
        className="warehouse-benefits-area"
        ref={sectionRefs.benefits}
        data-section="benefits"
      >
        <div className="warehouse-content-width">
          <div className="warehouse-center-align">
            <h2
              className={`warehouse-benefits-heading ${
                visibleSections.has("benefits") ? "warehouse-slide-up" : ""
              }`}
            >
              Key Benefits
            </h2>
            <p
              className={`warehouse-benefits-description ${
                visibleSections.has("benefits") ? "warehouse-fade-in" : ""
              }`}
            >
              Transform your warehouse operations with proven benefits and
              measurable results.
            </p>
          </div>

          <div className="warehouse-benefits-layout">
            {benefits.map((benefit, index) => (
              <div
                key={index}
                className={`warehouse-benefit-item ${
                  visibleSections.has("benefits") ? "warehouse-slide-up" : ""
                }`}
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                <CheckCircle className="warehouse-benefit-check-icon" />
                <span className="warehouse-benefit-text">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section
        className="warehouse-cta-area"
        ref={sectionRefs.cta}
        data-section="cta"
      >
        <div className="warehouse-content-width warehouse-center-align">
          <h2
            className={`warehouse-cta-heading ${
              visibleSections.has("cta") ? "warehouse-zoom-in" : ""
            }`}
          >
            Ready to Optimize Your Warehouse?
          </h2>
          <p
            className={`warehouse-cta-description ${
              visibleSections.has("cta") ? "warehouse-fade-in" : ""
            }`}
          >
            Let our warehouse management experts design a solution that meets
            your unique requirements and integrates seamlessly with Microsoft
            Dynamics NAV.
          </p>
          <div
            className={`warehouse-cta-buttons ${
              visibleSections.has("cta") ? "warehouse-bounce-in" : ""
            }`}
          >
            <button
              className="warehouse-primary-button"
              onClick={() => setShowContact(true)}
            >
              <Users className="warehouse-small-icon" />
              Schedule Consultation
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form Modal */}
      {showContact && (
        <div
          className="modal-overlay warehouse-fade-in"
          onClick={() => setShowContact(false)}
        >
          <div
            className="modal-content warehouse-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="modal-close"
              onClick={() => setShowContact(false)}
            >
              ✕
            </button>
            <ContactForm />
          </div>
        </div>
      )}
    </div>
  );
};

export default Warehouse;
